import { JsonPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  model,
  OnInit,
  SecurityContext,
  signal,
  TemplateRef,
  ViewChild,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MbscResource } from '@mobiscroll/angular';
import { RichTextEditorComponent } from '@softapps/core/rich-text';
import { ObjektFieldOkComponent } from '@softapps/wws/core';
import { handleRequestError, Step } from '@softline/application';
import { authorizationFn } from '@softline/auth';
import { DateService, IsoDate } from '@softline/core';
import { GroupInputComponent } from '@softline/dynamic';
import {
  ButtonComponent,
  CheckboxComponent,
  FieldValidationDirective,
  IconComponent,
  InputComponent,
  MultilineInputComponent,
  ResolvableModal,
  SoftSlotDirective,
  TranslatePipe,
  translator,
  ValidationContainerComponent,
  Validators,
} from '@softline/ui-core';
import { DateInputComponent, TimeInputComponent } from '@softline/ui-core/date-input';
import { TabComponent, TabGroupComponent } from '@softline/ui-core/tab-group';
import {
  MultiSelectComponent,
  SelectDsComponent,
  SelectDsOptionComponent,
} from '@softline/ui-core/select';
import { ProgressStepsComponent } from '@softline/ui-core/progress';
import {
  MarkdownComponent,
  MARKED_OPTIONS,
  provideMarkdown,
  SANITIZE,
} from 'ngx-markdown';
import { firstValueFrom, startWith } from 'rxjs';
import { SoftlineRosterResourceRepository } from '../../repositories/softline-roster-resource.repository';
import {
  EmployeeLookupRow,
  EmployeeLookupService,
} from '../../services/employee-lookup.service';
import { SoftlineObjectService } from '../../services/softline-object.service';
import { SoftlineTicketService } from '../../services/softline-ticket.service';
import { OldTicket } from '../../types/ticket';

interface EventFormData {
  eventType: string | null;
  comment: string | null;
  beginDay: string;
  beginTime: string;
  endDay: string;
  endTime: string;
  state: string;
  title?: string;
  isPrivate?: boolean;
  employeeId?: number;
  ticketId?: number;
  objectId?: number;
  type?: string;
  ticket?: string | null;
  object?: unknown;
  employee?: string;
  participants?: string[];
}

@Component({
  selector: 'soft-roster-event-form',
  templateUrl: './event-form.component.html',
  styleUrls: ['./event-form.component.css'],
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    FieldValidationDirective,
    DateInputComponent,
    TimeInputComponent,
    InputComponent,
    MultilineInputComponent,
    IconComponent,
    SelectDsOptionComponent,
    SelectDsComponent,
    SoftSlotDirective,
    CheckboxComponent,
    TabGroupComponent,
    TabComponent,
    MultiSelectComponent,
    ObjektFieldOkComponent,
    JsonPipe,
    RichTextEditorComponent,
    ProgressStepsComponent,
    GroupInputComponent,
    ValidationContainerComponent,
    TranslatePipe,
    MarkdownComponent,
  ],
  providers: [
    SoftlineRosterResourceRepository,
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: { gfm: true, breaks: true, pedantic: false },
      },
      sanitize: { provide: SANITIZE, useValue: SecurityContext.NONE },
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventFormComponent
  extends ResolvableModal<EventFormData | undefined>
  implements OnInit
{
  override readonly bottomTemplate = viewChild<TemplateRef<ViewChild>>('bottomOutlet');
  private readonly isAuthorized = authorizationFn();

  readonly userCanViewObjects = this.isAuthorized('ROSTER_CAN_VIEW_OBJECTS');

  private readonly t = translator();
  private readonly dateService = inject(DateService);
  private readonly resourceRepository = inject(SoftlineRosterResourceRepository);
  private readonly ticketService = inject(SoftlineTicketService);
  private readonly objectService = inject(SoftlineObjectService);
  private readonly employeeLookupService = inject(EmployeeLookupService);
  private readonly injector = inject(Injector);
  readonly date = input<IsoDate>();
  readonly resources = input.required<MbscResource[]>();
  readonly sortedResources = computed(() => {
    const byId = this.employeesById();
    return [...this.resources()].sort((a, b) => {
      const persnrA = byId.get(a.id + '')?.persnr;
      const persnrB = byId.get(b.id + '')?.persnr;
      if (persnrA != null && persnrB != null) return persnrA - persnrB;
      if (persnrA != null) return -1;
      if (persnrB != null) return 1;
      return (a['name'] as string).localeCompare(b['name'] as string);
    });
  });

  /** Display label "persnr - name" for a regular roster resource. */
  resourceLabel(resource: MbscResource): string {
    return this.displayName(resource.id, resource['name'] as string);
  }
  readonly employeeSearchQuery = signal<string | null | undefined>(null);
  readonly filteredResources = computed(() => {
    const q = this.employeeSearchQuery()?.toLowerCase();
    return q
      ? this.sortedResources().filter((r) =>
          this.resourceLabel(r).toLowerCase().includes(q)
        )
      : this.sortedResources();
  });
  readonly resource = input.required<(number | string)[]>();
  readonly eventType = model<string | undefined>();
  readonly editMode = input<boolean>(false);
  //TODO: add type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly event = input<any>(null);
  readonly navTabIndex = model<number>(0);

  readonly commentPreviewVisible = signal<boolean>(false);

  toggleCommentPreview(): void {
    this.commentPreviewVisible.update((v) => !v);
  }

  readonly steps = computed<Step[]>(() => {
    const isTermin = this.eventType() === 'Termin';
    const hasEmployee = isTermin
      ? (this.selectedParticipants()?.length ?? 0) > 0
      : !!this.form.value.employee;
    const hasDate =
      !!this.form.value.beginDay &&
      !!this.form.value.beginTime &&
      !!this.form.value.endTime;

    return [
      {
        header: this.t('#PMS_ROSTER.FORM.ENTRY_TYPE')(),
        subHeader: this.eventType()
          ? `${this.eventType()}`
          : this.t('#PMS_ROSTER.FORM.SELECT_ENTRY_TYPE')(),
        done: !!this.eventType(),
        execute: () => this.navTabIndex.set(0),
      },
      {
        header: isTermin
          ? this.t('#PMS_ROSTER.FORM.PARTICIPANT')()
          : this.t('#PMS_ROSTER.FORM.EMPLOYEE')(),
        subHeader: isTermin
          ? (this.selectedParticipants()?.length ?? 0) > 0
            ? `${this.selectedParticipants().length} ${this.t('#PMS_ROSTER.FORM.PARTICIPANTS_SELECTED')()}`
            : this.t('#PMS_ROSTER.FORM.CHOOSE_PARTICIPANTS')()
          : this._resource()
            ? `${this._resource()?.name}`
            : this.t('#PMS_ROSTER.FORM.CHOOSE_EMPLOYEE')(),
        done: hasEmployee,
        execute: () => this.navTabIndex.set(1),
      },
      {
        header: this.t('#PMS_ROSTER.FORM.DATE_TIME')(),
        subHeader: this.form.value.beginDay
          ? this.form.value.beginDay === this.form.value.endDay
            ? `${this.formatDate(this.form.value.beginDay)} ${this.formatTime(this.form.value.beginTime)} - ${this.formatTime(this.form.value.endTime)}`
            : `${this.formatDate(this.form.value.beginDay)} ${this.formatTime(this.form.value.beginTime)} - ${this.formatDate(this.form.value.endDay)} ${this.formatTime(this.form.value.endTime)}`
          : this.t('#PMS_ROSTER.FORM.SET_DATE_TIME')(),
        done: hasDate,
        execute: () => this.navTabIndex.set(2),
      },
      {
        header: isTermin
          ? this.t('#PMS_ROSTER.DETAILS')()
          : this.t('#PMS_ROSTER.FORM.TICKET_OBJECT')(),
        subHeader: isTermin
          ? this.t('#PMS_ROSTER.FORM.SUBJECT_DESCRIPTION')()
          : this.t('#PMS_ROSTER.FORM.LINK_ARTIFACT')(),
        done: isTermin ? !!this.titleValue()?.trim() : !!this.ticket() || !!this.object(),
        execute: () => this.navTabIndex.set(3),
      },
    ];
  });

  readonly form = new FormGroup({
    eventType: new FormControl<string>(this.eventType() || '', {
      validators: [],
    }),
    state: new FormControl<string>('UNDEFINED', {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    title: new FormControl<string>('', {
      nonNullable: true,
      validators: [],
    }),
    isPrivate: new FormControl<boolean>(false, {
      nonNullable: true,
    }),
    beginTime: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    beginDay: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    endDay: new FormControl<string>('', {
      nonNullable: true,
      validators: [],
    }),
    endTime: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    comment: new FormControl<string | null>(null),
    type: new FormControl<string>('PLAN', {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    ticket: new FormControl<string>('', {}),
    object: new FormControl<unknown>('', {}),
    employee: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    participants: new FormControl<string[]>([], {
      nonNullable: true,
    }),
  });

  constructor() {
    super();
    // Sync eventType model signal → form control
    effect(() => {
      this.form.controls.eventType.setValue(this.eventType() ?? '');
    });
    // Disable ticket input only while loading
    effect(() => {
      if (this.loading()) {
        this.form.controls.ticket.disable();
      } else {
        this.form.controls.ticket.enable();
      }
    });
    void this.loadEmployees();
  }

  /** Loads the lstfix employee list once to provide the personnel number for
      both the regular roster employees and the other-department participants. */
  private async loadEmployees(): Promise<void> {
    try {
      const employees = await firstValueFrom(this.employeeLookupService.query());
      this.employeesById.set(new Map(employees.map((e) => [e.id + '', e])));
    } catch {
      // Display names fall back to the plain roster name on failure.
    }
  }

  private readonly formStatus = toSignal(
    this.form.statusChanges.pipe(startWith(this.form.status)),
    { initialValue: this.form.status }
  );

  readonly commentValue = toSignal(
    this.form.controls.comment.valueChanges.pipe(
      startWith(this.form.controls.comment.value)
    ),
    { initialValue: this.form.controls.comment.value }
  );

  private readonly titleValue = toSignal(
    this.form.controls.title.valueChanges.pipe(startWith(this.form.controls.title.value)),
    { initialValue: this.form.controls.title.value }
  );

  readonly selectedParticipants = toSignal(
    this.form.controls.participants.valueChanges.pipe(
      startWith(this.form.controls.participants.value)
    ),
    { initialValue: this.form.controls.participants.value }
  );

  /** Employee rows from the lstfix field-ok, keyed by employee id, used to
      enrich the roster lists with the personnel number (persnr). */
  private readonly employeesById = signal<Map<string, EmployeeLookupRow>>(new Map());

  /** Display label "persnr - name" for an employee id, falling back to the name. */
  private displayName(id: string | number, fallbackName: string): string {
    const employee = this.employeesById().get(id + '');
    return employee ? `${employee.persnr} - ${employee.kurzbez}` : fallbackName;
  }

  /** Participant ids that are not part of the loaded department structure
      (employees from other departments). */
  readonly foreignParticipantIds = computed(() => {
    const known = new Set(this.resources().map((r) => r.id + ''));
    return this.resource()
      .map((id) => id + '')
      .filter((id) => !known.has(id));
  });

  /** Display names for foreign participants, resolved by id via the lstfix field-ok. */
  readonly foreignParticipants = computed(() => {
    const byId = this.employeesById();
    const all = this.foreignParticipantIds()
      .map((id) => ({ id, name: this.displayName(id, id) }))
      .sort((a, b) => {
        const persnrA = byId.get(a.id)?.persnr;
        const persnrB = byId.get(b.id)?.persnr;
        if (persnrA != null && persnrB != null) return persnrA - persnrB;
        if (persnrA != null) return -1;
        if (persnrB != null) return 1;
        return a.name.localeCompare(b.name);
      });
    const q = this.employeeSearchQuery()?.toLowerCase();
    return q ? all.filter((p) => p.name.toLowerCase().includes(q)) : all;
  });

  private readonly selectedEmployeeId = toSignal(
    this.form.controls.employee.valueChanges.pipe(
      startWith(this.form.controls.employee.value)
    ),
    { initialValue: this.form.controls.employee.value }
  );

  private readonly beginTimeSignal = toSignal(
    this.form.controls.beginTime.valueChanges.pipe(
      startWith(this.form.controls.beginTime.value)
    )
  );
  private readonly endTimeSignal = toSignal(
    this.form.controls.endTime.valueChanges.pipe(
      startWith(this.form.controls.endTime.value)
    )
  );

  readonly duration = computed(() => {
    const begin = this.beginTimeSignal();
    const end = this.endTimeSignal();
    if (!begin || !end) return null;
    const toMinutes = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + (m || 0);
    };
    const diff = toMinutes(end) - toMinutes(begin);
    if (diff <= 0) return null;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return h > 0 && m > 0 ? `${h}h ${m}min` : h > 0 ? `${h}h` : `${m}min`;
  });

  readonly isFormValid = computed(() => {
    const vals = this.form.getRawValue();
    if (!vals.beginDay || !vals.beginTime || !vals.endTime) return false;
    if (this.eventType() === 'Termin') {
      return !!vals.title && (this.selectedParticipants()?.length ?? 0) > 0;
    }
    return this.formStatus() === 'VALID';
  });

  public readonly statuses = computed(() => [
    { id: 'UNDEFINED', label: this.t('#PMS_ROSTER.STATUS_UNDEFINED')() },
    { id: 'DONE', label: this.t('#PMS_ROSTER.STATUS_DONE')() },
    { id: 'HANDOVER_TO_OTHERS', label: this.t('#PMS_ROSTER.STATUS_FORWARDED')() },
  ]);

  public readonly eventTypes = [
    { id: 'Planeintrag', label: 'Planeintrag' },
    { id: 'Termin', label: 'Termin' },
  ];

  public readonly typeOptions = [
    { label: 'Anruf', value: 'Anruf' },
    { label: 'Arzt', value: 'Arzt' },
    { label: 'Echtlauf', value: 'Echtlauf' },
    { label: 'Externe Besprechung', value: 'Externe Besprechung' },
    { label: 'Ferialarbeit', value: 'Ferialpraktikum' },
    { label: 'Home Office', value: 'Home Office' },
    { label: 'Hotline', value: 'Hotline' },
    { label: 'Installation', value: 'Installation' },
    { label: 'Interne Besprechung', value: 'Interne Besprechung' },
    { label: 'Krank', value: 'Krank' },
    { label: 'Kundentermin', value: 'Kundentermin' },
    { label: 'Kundentest Testsyste', value: 'Kundentest Testsystem' },
    { label: 'Offertbesprechung', value: 'Offertbesprechung' },
    { label: 'Programmieren', value: 'Programmieren' },
    { label: 'Programmstand', value: 'Aufsetzen Programmstand' },
    { label: 'Schulung', value: 'Schulung' },
    { label: 'Sport', value: 'Softline Sports' },
    { label: 'Test / Dokumentation', value: 'Test / Dokumentation' },
    { label: 'Urlaub', value: 'Urlaub' },
    { label: 'Vorführung', value: 'Vorführung' },
    { label: 'ZA', value: 'Zeitausgleich' },
    { label: 'externe Schulung', value: 'Externe Schulung' },
  ];

  readonly _resource = computed(() => {
    const employeeId = this.selectedEmployeeId();
    if (employeeId) {
      return this.resources()?.find((o) => o.id + '' === employeeId) ?? null;
    }
    const currentResource = this.resource()?.[0];
    return this.resources()?.find((o) => o.id === currentResource) ?? null;
  });

  private formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
  }

  private formatTime(timeStr: string | null | undefined): string {
    if (!timeStr) return '';
    return timeStr.slice(0, 5); // HH:mm
  }

  ngOnInit() {
    if (this.event()) {
      const start = new Date(this.event().start);
      const end = new Date(this.event().end);
      this.form.patchValue({
        beginDay: start.toISOString().split('T')[0],
        beginTime: start.toTimeString().slice(0, 8), // HH:mm:ss
        endDay: end.toISOString().split('T')[0],
        endTime: end.toTimeString().slice(0, 8),
        employee: this._resource()?.id ? this._resource()?.id + '' : '',
        state: this.event()?.additionalData?.state || 'UNDEFINED',
        title: this.event()?.title || '',
        comment: this.event()?.additionalData?.comment ?? this.event()?.text,
      });

      if (this.eventType() === 'Termin') {
        this.form.controls.participants.setValue(this.resource().map((id) => id + ''));
      }

      if (this.event()?.additionalData?.additionaldetail[0]?.tabname === 'TICKET') {
        const tabid = this.event()?.additionalData?.additionaldetail[0]?.tabid.toString();
        this.findTicket(tabid).catch(() => {
          this.form.controls.ticket.setValue(tabid ?? '');
        });
      } else if (this.event()?.additionalData?.additionaldetail[0]?.tabname === 'OBJECT')
        this.getObjectById(
          this.event()?.additionalData?.additionaldetail[0]?.tabid.toString()
        );
      return;
    }
    let dateStr = this.date();
    if (!dateStr) {
      dateStr = this.dateService.now() as IsoDate;
    }
    const today = dateStr ? new Date(dateStr) : new Date();
    const isFriday = today.getDay() === 5;
    this.form.patchValue({
      beginTime: '07:30:00',
      beginDay: today.toISOString().split('T')[0],
      endDay: today.toISOString().split('T')[0],
      endTime: isFriday ? '12:00:00' : '17:00:00',
      employee: this._resource()?.id ? this._resource()?.id + '' : '',
    });

    // Re-fetch workschema whenever the date changes (employee already selected)
    this.form.controls.beginDay.valueChanges.subscribe(async (date) => {
      const employeeId = this.form.controls.employee.value;
      if (date && employeeId) await this.applyWorkschema(date, +employeeId);
    });
  }
  public readonly ticket = signal<OldTicket | undefined>(undefined);
  /* eslint-disable @typescript-eslint/no-explicit-any */
  public readonly object = signal<any>(undefined);
  public readonly loading = signal<boolean>(false);

  async selectEmployee(employee: MbscResource): Promise<void> {
    this.form.controls.employee.setValue(employee.id + '');
    const date = this.form.controls.beginDay.value;
    if (date) await this.applyWorkschema(date, +employee.id);
  }

  private async applyWorkschema(date: string, employeeId: number): Promise<void> {
    try {
      const workschema = await this.resourceRepository.loadEmployeeWorkschema(
        date,
        employeeId
      );
      const dateObj = new Date(date);
      // ISO weekday: Mon=1 … Sun=7
      const weekday = dateObj.getDay() === 0 ? 7 : dateObj.getDay();
      const schema = workschema.detail.find((d) => d.weekday === weekday);
      if (schema) {
        this.form.patchValue({
          beginTime: schema.fromTime,
          endTime: schema.tillTime,
        });
      }
    } catch (error) {
      handleRequestError(error, this.injector, { show: true });
    }
  }

  toggleParticipant(id: string): void {
    const current = this.form.controls.participants.value ?? [];
    const idx = current.indexOf(id);
    if (idx >= 0) {
      this.form.controls.participants.setValue(current.filter((i) => i !== id));
    } else {
      this.form.controls.participants.setValue([...current, id]);
    }
  }

  ticketSearchKeydown = async (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      if (this.ticket() !== undefined || this.loading()) return;
      const inputElement = event.target as HTMLInputElement;
      try {
        await this.findTicket(inputElement.value);
      } catch {
        // error is already stored in ticketError signal
      }
    } else if (
      this.ticket() !== undefined &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      event.preventDefault();
      const ticketId = this.ticket()?.id?.toString() ?? '';
      this.ticket.set(undefined);
      this.object.set(undefined);
      this.ticketError.set(null);
      this.form.controls.ticket.setValue(ticketId);
    }
  };

  private pasteHandled = false;

  ticketPaste = (event: ClipboardEvent) => {
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.pasteHandled = true;
    if (this.ticketDebounceTimer) clearTimeout(this.ticketDebounceTimer);
    setTimeout(async () => {
      try {
        await this.findTicket(pasted);
      } catch {
        // error is already stored in ticketError signal
      }
      this.pasteHandled = false;
    }, 0);
  };

  private ticketDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ticketInputChange = (event: Event) => {
    if (this.pasteHandled) return;
    this.ticketError.set(null);
    this.ticket.set(undefined);
    const inputEl = event.target as HTMLInputElement;
    const value = inputEl.value.trim();
    if (this.ticketDebounceTimer) clearTimeout(this.ticketDebounceTimer);
    if (!value) return;
    this.ticketDebounceTimer = setTimeout(async () => {
      try {
        await this.findTicket(value, true);
      } catch {
        // error is already stored in ticketError signal
      }
    }, 400);
  };

  readonly ticketError = signal<string | null>(null);

  findTicket = async (searchTerm: string | null | undefined, silent = false) => {
    if (!silent) this.loading.set(true);
    this.ticketError.set(null);
    searchTerm = searchTerm?.trim().replace(/\D/g, '');
    try {
      const ticket = await this.ticketService.getTicketById(Number(searchTerm));
      this.ticket.set(ticket);
      this.form.controls.ticket.setValue(ticket?.id + ' - ' + ticket?.name || '');
      if (ticket?.object) {
        this.object.set({
          objekt: ticket.object.number + '',
          bezeichnung: ticket.object.name,
        });
      }
    } catch (error) {
      const apiMessage =
        (error as { error?: { errors?: { message?: string }[] } })?.error?.errors?.[0]
          ?.message ?? '';
      this.ticketError.set(apiMessage || this.t('#PMS_ROSTER.FORM.TICKET_NOT_FOUND')());
      throw error;
    } finally {
      if (!silent) this.loading.set(false);
    }
  };

  getObjectById = async (objectId: string) => {
    this.loading.set(true);
    try {
      const object = await this.objectService.getObjectById(objectId);
      this.object.set({
        id: object?.id,
        objekt: object?.key + '',
        bezeichnung: object?.name,
      });
      this.loading.set(false);
    } catch (error) {
      handleRequestError(error, this.injector, { show: true });
      this.loading.set(false);
    }
  };

  onEnter(event: Event): void {
    const target = event.target as HTMLElement;
    // Don't intercept Enter on multiline inputs
    if (target.tagName === 'TEXTAREA') return;
    event.preventDefault();
    this.advance();
  }

  advance(): void {
    switch (this.navTabIndex()) {
      case 0:
        if (this.eventType()) this.navTabIndex.set(1);
        break;
      case 1:
        if (
          this.eventType() === 'Planeintrag'
            ? this.form.value.employee
            : this.selectedParticipants()?.length
        )
          this.navTabIndex.set(2);
        break;
      case 2:
        if (
          this.form.value.beginDay &&
          this.form.value.beginTime &&
          this.form.value.endTime
        )
          this.navTabIndex.set(3);
        break;
      case 3:
        if (this.isFormValid() && !this.ticketError()) this.save();
        break;
    }
  }

  async save() {
    if (this.eventType() === 'Planeintrag') {
      await this.savePlanEntry();
    } else {
      this.saveAppointment();
    }
  }

  savePlanEntry = async () => {
    if (this.ticketError()) return;

    const ticketInput = this.form.controls.ticket.value?.trim();
    const rawTicketId = ticketInput
      ? Number(ticketInput.trim().split(/\D/)[0]) || undefined
      : undefined;
    if (!this.ticket() && ticketInput) {
      try {
        await this.findTicket(ticketInput);
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return;
      }
    }

    const formValues = this.form.getRawValue();
    this.resolve({
      beginDay: formValues.beginDay,
      endDay: formValues.beginDay,
      beginTime: formValues.beginTime,
      endTime: formValues.endTime,
      employeeId: Number(formValues.employee),
      ticketId: this.ticket()?.id ?? rawTicketId,
      objectId: Number((this.object() as { id?: number })?.id),
      eventType: this.eventType() ?? 'Planeintrag',
      state: formValues.state,
      comment: formValues.comment,
    });
  };

  saveAppointment = () => {
    this.resolve(this.form.getRawValue());
  };

  close() {
    this.resolve(undefined);
  }
}
